import { cellKey, type CellStack, type MegaBlockDef, type LockedDoorDef, type BlockadeWallDef, type BarricadeDef } from './BoardSystem';
import { resetAttackerIds } from '../entities/Attacker';
import { createSeededRng } from '../utils/seededRandom';
import { computeColorReachability, type ReachabilityOptions } from './Reachability';

export interface LevelConfig {
  rows: number;
  cols: number;
  numColors: number;
  maxStackDepth: number;
  seed: number;
}

export interface DifficultyConfig {
  multiSlotChance: number;   // 0-1, total probability of multi-slot (size 2 or 3)
  multiColorChance: number;  // 0-1, probability of merging into multi-color
  mysteryChance: number;     // 0-1, probability of an attacker having mystery orbs
  frozenChance: number;      // 0-1, probability of an attacker being frozen
  minAmmo: number;           // minimum ammo per attacker
  maxAmmo: number;           // maximum ammo per attacker
  ammoSurplusRatio: number;  // multiplier for total ammo per color (1.0 = exact, 1.3 = 30% surplus)
  /**
   * 0-1, **conditional** probability that an attacker which is *already*
   * multi-slot (size 2 or 3, produced by `multiSlotChance`) has its orbs split
   * across several colors. It never creates a multi-orb attacker on its own —
   * it only decides whether an existing one is mono- or multi-color, which is
   * why it is not `multiColorChance` (that knob is the separate merge pass at
   * the end of `generateAttackerQueue`, which fuses two *size-1* entries).
   *
   * Setting it to 0 stops *this* pass from producing multi-color attackers —
   * multi-orb attackers still appear, produced by `multiSlotChance` alone. It
   * does NOT get you a multi-color-free queue on its own: the `multiColorChance`
   * merge pass keeps fusing size-1 pairs, and every shipped tier from level 5 up
   * has `multiColorChance > 0` (measured over 60 seeds on an 8x8 4-color board:
   * tier@lvl30 goes 275 → 48 multi-color, tier@lvl80 goes 417 → 25, not 0).
   * To sweep `multiSlotChance` with multi-color fully held out, zero BOTH knobs.
   *
   * The realised rate is lower than this value: the whole pass is gated on
   * `colors.length >= 2`, the adjacency-spacing rule skips candidates, and a
   * later pass splits surviving adjacent multi-orb pairs back apart.
   *
   * Omitted ⇒ {@link DEFAULT_MULTI_SLOT_COLOR_SPLIT_CHANCE} (0.9), the rate
   * that was hardcoded before this became a parameter — so every existing
   * level and difficulty tier generates a byte-identical queue.
   */
  multiSlotColorSplitChance?: number;
}

/**
 * Historical hardcoded rate at which a multi-slot attacker was converted to
 * multi-color. Kept as the default so omitting
 * `DifficultyConfig.multiSlotColorSplitChance` reproduces the old queues exactly.
 */
export const DEFAULT_MULTI_SLOT_COLOR_SPLIT_CHANCE = 0.9;

/**
 * 12-tier difficulty progression for a smooth curve from beginner to expert.
 * Each tier specifies the level range and difficulty parameters.
 * Interpolation within each tier ensures no sudden jumps.
 */
interface DifficultyTier {
  maxLevel: number;       // last level in this tier (inclusive)
  multiSlotChance: number;
  multiColorChance: number;
  mysteryChance: number;
  frozenChance: number;
  minAmmo: number;
  maxAmmo: number;
  ammoSurplusRatio: number;
}

const DIFFICULTY_TIERS: DifficultyTier[] = [
  // Tier 1 (levels 1-2): Intro — single-slot only
  { maxLevel: 2,   multiSlotChance: 0,    multiColorChance: 0,    mysteryChance: 0,    frozenChance: 0,    minAmmo: 5, maxAmmo: 8,  ammoSurplusRatio: 1.0 },
  // Tier 2 (levels 3-4): Early — multi-slot begins
  { maxLevel: 4,   multiSlotChance: 0.10, multiColorChance: 0,    mysteryChance: 0,    frozenChance: 0,    minAmmo: 4, maxAmmo: 7,  ammoSurplusRatio: 1.0 },
  // Tier 3 (levels 5-7): Pressure — multi-color begins, real multi-slot
  { maxLevel: 7,   multiSlotChance: 0.15, multiColorChance: 0.05, mysteryChance: 0,    frozenChance: 0,    minAmmo: 4, maxAmmo: 7,  ammoSurplusRatio: 1.0 },
  // Tier 4 (levels 8-11): Challenging — multi-slot ramps, first mystery
  { maxLevel: 11,  multiSlotChance: 0.20, multiColorChance: 0.08, mysteryChance: 0.03, frozenChance: 0,    minAmmo: 3, maxAmmo: 7,  ammoSurplusRatio: 1.0 },
  // Tier 5 (levels 12-16): Medium — substantial multi-slot, real multi-color
  { maxLevel: 16,  multiSlotChance: 0.25, multiColorChance: 0.12, mysteryChance: 0.05, frozenChance: 0,    minAmmo: 3, maxAmmo: 6,  ammoSurplusRatio: 1.0 },
  // Tier 6 (levels 17-22): Medium-Hard — heavy multi-slot, mystery present, frozen begins
  { maxLevel: 22,  multiSlotChance: 0.30, multiColorChance: 0.15, mysteryChance: 0.08, frozenChance: 0.05, minAmmo: 3, maxAmmo: 6,  ammoSurplusRatio: 1.0 },
  // Tier 7 (levels 23-30): Hard — frequent multi-slot, tight ammo, more frozen
  { maxLevel: 30,  multiSlotChance: 0.35, multiColorChance: 0.18, mysteryChance: 0.10, frozenChance: 0.08, minAmmo: 3, maxAmmo: 5,  ammoSurplusRatio: 1.0 },
  // Tier 8 (levels 31-40): Hard+ — very frequent multi-slot, real multi-color
  { maxLevel: 40,  multiSlotChance: 0.40, multiColorChance: 0.20, mysteryChance: 0.12, frozenChance: 0.10, minAmmo: 2, maxAmmo: 5,  ammoSurplusRatio: 1.0 },
  // Tier 9 (levels 41-52): Expert — heavy multi-slot/color
  { maxLevel: 52,  multiSlotChance: 0.45, multiColorChance: 0.22, mysteryChance: 0.15, frozenChance: 0.12, minAmmo: 2, maxAmmo: 5,  ammoSurplusRatio: 1.0 },
  // Tier 10 (levels 53-64): Expert+ — dominant multi-slot
  { maxLevel: 64,  multiSlotChance: 0.50, multiColorChance: 0.24, mysteryChance: 0.17, frozenChance: 0.14, minAmmo: 2, maxAmmo: 4,  ammoSurplusRatio: 1.0 },
  // Tier 11 (levels 65-76): Master — near-perfect play required
  { maxLevel: 76,  multiSlotChance: 0.53, multiColorChance: 0.25, mysteryChance: 0.18, frozenChance: 0.16, minAmmo: 2, maxAmmo: 4,  ammoSurplusRatio: 1.0 },
  // Tier 12 (levels 77+): Grandmaster — maximum challenge, razor-thin margins
  { maxLevel: Infinity, multiSlotChance: 0.55, multiColorChance: 0.25, mysteryChance: 0.20, frozenChance: 0.18, minAmmo: 2, maxAmmo: 4, ammoSurplusRatio: 1.0 },
];

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function getDifficultyForLevel(level: number): DifficultyConfig {
  // Find which tier this level falls in, and the previous tier for interpolation
  let prevTier = DIFFICULTY_TIERS[0];
  let prevMaxLevel = 0;

  for (const tier of DIFFICULTY_TIERS) {
    if (level <= tier.maxLevel) {
      // Interpolate within the tier range for a smooth curve
      const rangeStart = prevMaxLevel + 1;
      const rangeSize = tier.maxLevel === Infinity ? 1 : tier.maxLevel - prevMaxLevel;
      const t = rangeSize > 0 ? Math.min((level - rangeStart) / rangeSize, 1) : 0;

      return {
        multiSlotChance: lerp(prevTier.multiSlotChance, tier.multiSlotChance, t),
        multiColorChance: lerp(prevTier.multiColorChance, tier.multiColorChance, t),
        mysteryChance: lerp(prevTier.mysteryChance, tier.mysteryChance, t),
        frozenChance: lerp(prevTier.frozenChance, tier.frozenChance, t),
        minAmmo: Math.round(lerp(prevTier.minAmmo, tier.minAmmo, t)),
        maxAmmo: Math.round(lerp(prevTier.maxAmmo, tier.maxAmmo, t)),
        ammoSurplusRatio: lerp(prevTier.ammoSurplusRatio, tier.ammoSurplusRatio, t),
      };
    }
    prevTier = tier;
    prevMaxLevel = tier.maxLevel;
  }

  // Fallback (shouldn't reach here due to Infinity tier)
  const last = DIFFICULTY_TIERS[DIFFICULTY_TIERS.length - 1];
  return {
    multiSlotChance: last.multiSlotChance,
    multiColorChance: last.multiColorChance,
    mysteryChance: last.mysteryChance,
    frozenChance: last.frozenChance,
    minAmmo: last.minAmmo,
    maxAmmo: last.maxAmmo,
    ammoSurplusRatio: last.ammoSurplusRatio,
  };
}

/** Returns the 1-based tier number for a given level. */
export function getTierForLevel(level: number): number {
  for (let i = 0; i < DIFFICULTY_TIERS.length; i++) {
    if (level <= DIFFICULTY_TIERS[i].maxLevel) {
      return i + 1;
    }
  }
  return DIFFICULTY_TIERS.length;
}

export interface AttackerQueueEntry {
  colorIndex: number;
  ammo: number;
  size: number; // 1, 2, or 3
  orbs?: { colorIndex: number; ammo: number; mystery?: boolean }[]; // multi-color orbs (overrides colorIndex/ammo)
  mystery?: boolean; // if true, all orbs on this attacker are mystery (for single-color entries without explicit orbs)
  frozen?: number; // defrost counter — number of attacker exhaustions needed to thaw. 0 or undefined = not frozen
}

export interface LevelData {
  grid: CellStack[][];
  attackerQueue: AttackerQueueEntry[];
  config: LevelConfig;
  /** Set of numeric cell keys for cells that are part of snake blocks. */
  snakeCells?: Set<number>;
  /** Definitions for mega blocks placed on the board. */
  megaBlockDefs?: MegaBlockDef[];
  /** Definitions for locked doors placed on the board. */
  lockedDoorDefs?: LockedDoorDef[];
  /** Definitions for blockade walls on board edges. */
  blockadeWallDefs?: BlockadeWallDef[];
  /** Definitions for barricades placed on the board (broken by hammers only). */
  barricadeDefs?: BarricadeDef[];
  /** Queue indices where hammer items should be inserted. */
  hammerIndices?: number[];
}

function createRng(seed: number): () => number {
  const rng = createSeededRng(seed);
  return rng.random;
}

/**
 * Generate an attacker queue with enough ammo per color to clear all cubes.
 * Shared by both random generation and image-based levels.
 */
export function generateAttackerQueue(
  grid: CellStack[][],
  seed: number,
  difficulty?: DifficultyConfig,
  megaBlockDefs?: MegaBlockDef[],
  blockadeWallDefs?: BlockadeWallDef[],
): AttackerQueueEntry[] {
  const rng = createRng(seed);
  // Default difficulty matches the original hardcoded values
  const diff = difficulty ?? { multiSlotChance: 0.45, multiColorChance: 0.15, mysteryChance: 0, frozenChance: 0, minAmmo: 4, maxAmmo: 13, ammoSurplusRatio: 1.0 };

  // Build set of cells occupied by mega blocks (don't double-count them)
  const megaCells = new Set<string>();
  if (megaBlockDefs) {
    for (const def of megaBlockDefs) {
      for (let r = def.row; r < def.row + def.height; r++) {
        for (let c = def.col; c < def.col + def.width; c++) {
          megaCells.add(`${r},${c}`);
        }
      }
    }
  }

  // Count cubes per color, excluding mega block cells
  const cubeCounts = new Map<number, number>();
  for (let r = 0; r < grid.length; r++) {
    for (let c = 0; c < grid[r].length; c++) {
      if (megaCells.has(`${r},${c}`)) continue;
      for (const color of grid[r][c]) {
        cubeCounts.set(color, (cubeCounts.get(color) ?? 0) + 1);
      }
    }
  }
  // Add mega block HP to their color counts
  if (megaBlockDefs) {
    for (const def of megaBlockDefs) {
      cubeCounts.set(def.colorIndex, (cubeCounts.get(def.colorIndex) ?? 0) + def.hp);
    }
  }
  // Add blockade wall HP to their color counts (walls consume ammo of their color)
  if (blockadeWallDefs) {
    for (const def of blockadeWallDefs) {
      if (def.colorIndex !== undefined) {
        cubeCounts.set(def.colorIndex, (cubeCounts.get(def.colorIndex) ?? 0) + def.hp);
      }
    }
  }

  // Collect all distinct colors present in the grid
  const colors = Array.from(cubeCounts.keys()).sort((a, b) => a - b);

  // Generate attacker queue with difficulty-controlled parameters
  // Total ammo per color = exact cube count (no surplus)
  const ammoRange = diff.maxAmmo - diff.minAmmo + 1;
  const attackerQueue: AttackerQueueEntry[] = [];
  for (const color of colors) {
    const cubeCount = cubeCounts.get(color) ?? 0;
    const totalAmmoTarget = cubeCount;
    let remaining = totalAmmoTarget;
    while (remaining > 0) {
      const sizeRoll = rng();
      // 1/3 of multiSlotChance → size 3, rest → size 2
      let size = sizeRoll < diff.multiSlotChance * 0.33 ? 3
               : sizeRoll < diff.multiSlotChance ? 2
               : 1;
      // Clamp size down if not enough ammo remaining for each orb to get at least 1
      if (remaining < size) size = remaining;
      // Ensure minimum ammo is at least size so every orb gets at least 1
      const minForSize = Math.max(diff.minAmmo, size);
      const ammo = Math.min(remaining, minForSize + Math.floor(rng() * ammoRange));
      attackerQueue.push({ colorIndex: color, ammo, size });
      remaining -= ammo;
    }
  }

  // ── Reachability-biased ordering ──
  // Instead of pure random shuffle, group by color, sort groups by reachability
  // (most reachable first), shuffle within groups, then interleave round-robin.
  {
    const reachOptions: ReachabilityOptions = {};
    if (megaBlockDefs) reachOptions.megaBlockDefs = megaBlockDefs;
    if (blockadeWallDefs) reachOptions.blockadeWalls = blockadeWallDefs;

    const reachability = computeColorReachability(grid, reachOptions);

    // Group attackers by primary color
    const groups = new Map<number, AttackerQueueEntry[]>();
    for (const entry of attackerQueue) {
      const color = entry.colorIndex;
      if (!groups.has(color)) groups.set(color, []);
      groups.get(color)!.push(entry);
    }

    // Sort groups by reachability ratio (most reachable first)
    const sortedColors = Array.from(groups.keys()).sort((a, b) => {
      const ra = reachability.get(a);
      const rb = reachability.get(b);
      const ratioA = ra ? ra.reachable / Math.max(ra.total, 1) : 0;
      const ratioB = rb ? rb.reachable / Math.max(rb.total, 1) : 0;
      return ratioB - ratioA; // descending
    });

    // Shuffle within each group (local randomization)
    for (const entries of groups.values()) {
      for (let i = entries.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [entries[i], entries[j]] = [entries[j], entries[i]];
      }
    }

    // Interleave: round-robin from sorted groups
    attackerQueue.length = 0;
    const groupArrays = sortedColors.map(c => groups.get(c)!);
    const indices = new Array(groupArrays.length).fill(0);
    let placed = 0;
    const totalEntries = groupArrays.reduce((s, g) => s + g.length, 0);

    while (placed < totalEntries) {
      for (let g = 0; g < groupArrays.length; g++) {
        if (indices[g] < groupArrays[g].length) {
          attackerQueue.push(groupArrays[g][indices[g]++]);
          placed++;
        }
      }
    }
  }

  // Post-process: make multi-slot (size 2/3) attackers multi-color at the
  // `multiSlotColorSplitChance` rate (default 0.9 — the historical hardcoded value).
  // This is conditional on the attacker already being multi-slot; it never creates one.
  // Primary orb keeps the original color with most ammo; secondary orbs each get 1 ammo
  // in a different color. We track the per-color ammo deficit and top up afterwards.
  // Secondary colors are sampled uniformly from all other colors (no reachability bias).
  // Constraint: enforce adjacency spacing (no two adjacent multi-orb attackers).
  const colorSplitChance = diff.multiSlotColorSplitChance ?? DEFAULT_MULTI_SLOT_COLOR_SPLIT_CHANCE;
  if (colors.length >= 2) {
    let lastWasMulti = false; // Track adjacency for spacing constraint

    for (let i = 0; i < attackerQueue.length; i++) {
      const entry = attackerQueue[i];
      // NOTE: `&&` short-circuits, so `rng()` is consumed on exactly the same
      // entries (and in the same order) as when this rate was hardcoded.
      if (entry.size > 1 && !entry.orbs && rng() < colorSplitChance) {
        // Constraint: no two adjacent multi-orb attackers
        if (lastWasMulti) {
          lastWasMulti = false;
          continue; // skip this one, keep as single-color multi-slot
        }

        const secondaryCount = entry.size - 1; // 1 for size-2, 2 for size-3

        // Uniformly sample secondary colors from all colors other than the primary.
        const available = colors.filter(c => c !== entry.colorIndex);

        const secondaryColors: number[] = [];
        const pool = [...available];
        for (let s = 0; s < secondaryCount; s++) {
          if (pool.length > 0) {
            const idx = Math.floor(rng() * pool.length);
            secondaryColors.push(pool[idx]);
            pool.splice(idx, 1); // don't repeat within same attacker
          } else {
            // Fallback: all distinct colors exhausted, pick any different color
            const fallback = colors[Math.floor(rng() * colors.length)];
            secondaryColors.push(fallback);
          }
        }

        if (secondaryColors.length < secondaryCount) {
          lastWasMulti = false;
          continue;
        }

        // Apply conversion
        const primaryAmmo = entry.ammo - secondaryCount; // at least 1 (since ammo >= size)
        const orbs: { colorIndex: number; ammo: number }[] = [
          { colorIndex: entry.colorIndex, ammo: primaryAmmo },
        ];
        for (const sc of secondaryColors) {
          orbs.push({ colorIndex: sc, ammo: 1 });
        }
        attackerQueue[i] = {
          colorIndex: entry.colorIndex,
          ammo: entry.ammo,
          size: entry.size,
          orbs,
        };
        lastWasMulti = true;
      } else {
        lastWasMulti = entry.size > 1 && !!entry.orbs;
      }
    }
  }

  // Rebalance: ensure each color's total ammo exactly matches its cube count after multi-color reassignment.
  // Multi-color conversion moves ammo from primary colors to secondary colors, creating deficits and surpluses.
  // This pass corrects both: trims surplus by reducing the last attacker of that color, adds deficit attackers.
  // The net effect is zero-sum: total ammo across all colors === total cubes on the board.
  {
    // Recompute ammo per color
    const ammoByColor = new Map<number, number>();
    for (const entry of attackerQueue) {
      if (entry.orbs) {
        for (const orb of entry.orbs) {
          ammoByColor.set(orb.colorIndex, (ammoByColor.get(orb.colorIndex) ?? 0) + orb.ammo);
        }
      } else {
        ammoByColor.set(entry.colorIndex, (ammoByColor.get(entry.colorIndex) ?? 0) + entry.ammo);
      }
    }

    // Trim colors with surplus ammo (secondary orbs received extra from other colors' budgets)
    for (const color of colors) {
      const target = cubeCounts.get(color) ?? 0;
      const current = ammoByColor.get(color) ?? 0;
      let surplus = current - target;
      if (surplus <= 0) continue;

      // Walk backwards through the queue trimming ammo from this color's orbs
      for (let i = attackerQueue.length - 1; i >= 0 && surplus > 0; i--) {
        const entry = attackerQueue[i];
        if (entry.orbs) {
          for (const orb of entry.orbs) {
            if (orb.colorIndex === color && orb.ammo > 0) {
              const trim = Math.min(surplus, orb.ammo);
              orb.ammo -= trim;
              entry.ammo -= trim;
              surplus -= trim;
            }
          }
        } else if (entry.colorIndex === color && entry.ammo > 0) {
          const trim = Math.min(surplus, entry.ammo);
          entry.ammo -= trim;
          surplus -= trim;
        }
      }
    }

    // Remove any attackers / orbs that ended up with 0 total ammo
    for (let i = attackerQueue.length - 1; i >= 0; i--) {
      const entry = attackerQueue[i];
      if (entry.orbs) {
        // Remove orbs with 0 ammo
        entry.orbs = entry.orbs.filter(o => o.ammo > 0);
        // Update size to match remaining orbs
        entry.size = entry.orbs.length;
        // If all orbs removed, or total ammo is 0, remove the entry
        if (entry.orbs.length === 0 || entry.ammo <= 0) {
          attackerQueue.splice(i, 1);
          continue;
        }
        // If only 1 orb left, simplify to single-color
        if (entry.orbs.length === 1) {
          entry.colorIndex = entry.orbs[0].colorIndex;
          entry.ammo = entry.orbs[0].ammo;
          entry.size = 1;
          delete entry.orbs;
        }
      } else if (entry.ammo <= 0) {
        attackerQueue.splice(i, 1);
      }
    }

    // Recompute after trimming
    const ammoAfterTrim = new Map<number, number>();
    for (const entry of attackerQueue) {
      if (entry.orbs) {
        for (const orb of entry.orbs) {
          ammoAfterTrim.set(orb.colorIndex, (ammoAfterTrim.get(orb.colorIndex) ?? 0) + orb.ammo);
        }
      } else {
        ammoAfterTrim.set(entry.colorIndex, (ammoAfterTrim.get(entry.colorIndex) ?? 0) + entry.ammo);
      }
    }

    // Top up any color that fell below its target (primary colors that donated to secondary orbs)
    for (const color of colors) {
      const target = cubeCounts.get(color) ?? 0;
      const current = ammoAfterTrim.get(color) ?? 0;
      if (current < target) {
        const deficit = target - current;
        attackerQueue.push({ colorIndex: color, ammo: deficit, size: 1 });
      }
    }
  }

  // Post-process: merge some consecutive size-1 entries into multi-color size-2 attackers
  // Enforces adjacency constraint: no two multi-orb attackers adjacent.
  if (colors.length >= 2 && diff.multiColorChance > 0) {
    for (let i = 0; i < attackerQueue.length - 1; i++) {
      const a = attackerQueue[i];
      const b = attackerQueue[i + 1];
      // Check adjacency: skip if previous entry is already multi-orb
      const prevIsMulti = i > 0 && attackerQueue[i - 1].size > 1;
      if (prevIsMulti) continue;
      if (a.size === 1 && b.size === 1 && !a.orbs && !b.orbs &&
          a.colorIndex !== b.colorIndex && rng() < diff.multiColorChance) {
        attackerQueue[i] = {
          colorIndex: a.colorIndex,
          ammo: a.ammo + b.ammo,
          size: 2,
          orbs: [
            { colorIndex: a.colorIndex, ammo: a.ammo },
            { colorIndex: b.colorIndex, ammo: b.ammo },
          ],
        };
        attackerQueue.splice(i + 1, 1);
        // Skip next entry to maintain adjacency spacing
        i++;
      }
    }
  }

  // Post-process: enforce multi-orb adjacency spacing.
  // Rebalancing may have removed entries between multi-orb attackers, breaking spacing.
  // Walk through and split the second of any two adjacent multi-orb entries into
  // separate single-color entries (preserving ammo per color exactly).
  {
    for (let i = 1; i < attackerQueue.length; i++) {
      const prev = attackerQueue[i - 1];
      const curr = attackerQueue[i];
      if (prev.orbs && prev.orbs.length > 1 && curr.orbs && curr.orbs.length > 1) {
        // Split curr into individual single-color entries for each orb
        const replacements: AttackerQueueEntry[] = curr.orbs.map(orb => ({
          colorIndex: orb.colorIndex,
          ammo: orb.ammo,
          size: 1,
        }));
        attackerQueue.splice(i, 1, ...replacements);
        // No need to re-check: replacements are all size-1 single-color
      }
    }
  }

  // Post-process: flag some attackers as mystery (hidden color until first shot)
  if (diff.mysteryChance > 0) {
    for (const entry of attackerQueue) {
      if (rng() < diff.mysteryChance) {
        if (entry.orbs) {
          // Multi-color: flag each orb as mystery
          for (const orb of entry.orbs) {
            orb.mystery = true;
          }
        } else {
          // Single-color: flag the whole entry
          entry.mystery = true;
        }
      }
    }
  }

  // Post-process: freeze some attackers (hidden until defrosted by attacker exhaustions)
  if (diff.frozenChance > 0) {
    for (let i = 0; i < attackerQueue.length; i++) {
      // Only freeze attackers that aren't at the very front (need at least a few before them)
      if (i < 2) continue;
      if (rng() < diff.frozenChance) {
        // Defrost counter: 2-4, scaled loosely by how far back in queue
        const counter = 2 + Math.floor(rng() * 3); // 2, 3, or 4
        attackerQueue[i].frozen = counter;
      }
    }
  }

  // Final invariant: total ammo per color must exactly equal cube count.
  // Clamp any remaining rounding drift on the last attacker of each color.
  {
    const finalAmmo = new Map<number, number>();
    for (const entry of attackerQueue) {
      if (entry.orbs) {
        for (const orb of entry.orbs) {
          finalAmmo.set(orb.colorIndex, (finalAmmo.get(orb.colorIndex) ?? 0) + orb.ammo);
        }
      } else {
        finalAmmo.set(entry.colorIndex, (finalAmmo.get(entry.colorIndex) ?? 0) + entry.ammo);
      }
    }
    for (const color of colors) {
      const target = cubeCounts.get(color) ?? 0;
      const actual = finalAmmo.get(color) ?? 0;
      let drift = actual - target;
      if (drift === 0) continue;
      // Walk backwards and adjust ammo on the last attacker(s) with this color
      for (let i = attackerQueue.length - 1; i >= 0 && drift !== 0; i--) {
        const entry = attackerQueue[i];
        if (entry.orbs) {
          for (const orb of entry.orbs) {
            if (orb.colorIndex === color) {
              const maxAdj = orb.ammo - 1; // keep at least 1
              const adj = Math.min(drift, maxAdj);
              if (adj > 0) {
                orb.ammo -= adj;
                entry.ammo -= adj;
                drift -= adj;
              }
              break; // only adjust first matching orb per entry
            }
          }
        } else if (entry.colorIndex === color) {
          const maxAdj = entry.ammo - 1;
          const adj = Math.min(drift, maxAdj);
          if (adj > 0) {
            entry.ammo -= adj;
            drift -= adj;
          }
        }
      }
    }
  }

  return attackerQueue;
}

export interface FromGridOptions {
  imageGrid: number[][];
  seed?: number;
  difficulty?: DifficultyConfig;
  snakeCols?: number[];
  megaBlockDefs?: MegaBlockDef[];
  lockedDoorDefs?: LockedDoorDef[];
  blockadeWallDefs?: BlockadeWallDef[];
  barricadeDefs?: BarricadeDef[];
  hammerIndices?: number[];
}

export class LevelGenerator {
  static generate(config: LevelConfig, difficulty?: DifficultyConfig): LevelData {
    resetAttackerIds();
    const rng = createRng(config.seed);

    // Build grid
    const grid: CellStack[][] = [];
    for (let r = 0; r < config.rows; r++) {
      const row: CellStack[] = [];
      for (let c = 0; c < config.cols; c++) {
        const depth = 1 + Math.floor(rng() * config.maxStackDepth);
        const stack: number[] = [];
        for (let d = 0; d < depth; d++) {
          stack.push(Math.floor(rng() * config.numColors));
        }
        row.push(stack);
      }
      grid.push(row);
    }

    const attackerQueue = generateAttackerQueue(grid, config.seed + 1, difficulty);

    return { grid, attackerQueue, config };
  }

  /**
   * Create a level from a pre-built grid (e.g. from pixel art or PNG import).
   * Grid values: -1 = empty cell, 0+ = color index (see GAME_COLORS).
   * snakeCols: column indices in the source grid whose filled cells become snake blocks.
   */
  static fromGrid(options: FromGridOptions): LevelData {
    const { imageGrid, seed, difficulty, snakeCols, megaBlockDefs, lockedDoorDefs, blockadeWallDefs, barricadeDefs, hammerIndices } = options;
    resetAttackerIds();
    const actualSeed = seed ?? Date.now();

    // Convert number[][] to CellStack[][]
    const grid: CellStack[][] = imageGrid.map(row =>
      row.map(val => (val < 0 ? [] : [val]))
    );

    const rows = grid.length;
    const cols = grid[0]?.length ?? 0;

    // Build snakeCells set from snakeCols
    let snakeCells: Set<number> | undefined;
    if (snakeCols && snakeCols.length > 0) {
      snakeCells = new Set<number>();
      const colSet = new Set(snakeCols);
      for (let col = 0; col < cols; col++) {
        if (!colSet.has(col)) continue;
        for (let row = 0; row < rows; row++) {
          if (grid[row][col].length > 0) {
            snakeCells.add(cellKey(row, col));
          }
        }
      }
    }

    // Count distinct colors to determine numColors
    const colorSet = new Set<number>();
    for (const row of imageGrid) {
      for (const val of row) {
        if (val >= 0) colorSet.add(val);
      }
    }

    // Also count colors from mega blocks
    if (megaBlockDefs) {
      for (const def of megaBlockDefs) {
        colorSet.add(def.colorIndex);
      }
    }

    // Minimum 2 colors safeguard: if only 1 color exists, add a second color
    // by converting border/edge cubes to a contrasting color.
    if (colorSet.size === 1) {
      const existingColor = Array.from(colorSet)[0];
      // Pick a second color that contrasts with the existing one
      // Color map: 0=red, 1=blue, 2=green, 3=yellow, 4=purple, 5=orange, 6=cyan, 7=pink, 8=white
      const contrastMap: Record<number, number> = {
        0: 1, 1: 5, 2: 4, 3: 4, 4: 3, 5: 1, 6: 0, 7: 1, 8: 1, 9: 5, 10: 3, 11: 4,
      };
      const secondColor = contrastMap[existingColor] ?? ((existingColor + 3) % 12);
      colorSet.add(secondColor);

      // Convert ~20% of edge cubes to the second color to create a border effect
      const rngLocal = createRng(actualSeed + 999);
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          if (grid[r][c].length === 0) continue; // skip empty cells
          // Check if this is an edge cell (adjacent to empty or grid boundary)
          const isEdge =
            r === 0 || r === rows - 1 || c === 0 || c === cols - 1 ||
            (r > 0 && grid[r - 1][c].length === 0) ||
            (r < rows - 1 && grid[r + 1][c].length === 0) ||
            (c > 0 && grid[r][c - 1].length === 0) ||
            (c < cols - 1 && grid[r][c + 1].length === 0);
          if (isEdge && rngLocal() < 0.35) {
            grid[r][c] = [secondColor];
          }
        }
      }
    }

    const attackerQueue = generateAttackerQueue(grid, actualSeed, difficulty, megaBlockDefs, blockadeWallDefs);

    const config: LevelConfig = {
      rows,
      cols,
      numColors: colorSet.size,
      maxStackDepth: 1,
      seed: actualSeed,
    };

    return { grid, attackerQueue, config, snakeCells, megaBlockDefs, lockedDoorDefs, blockadeWallDefs, barricadeDefs, hammerIndices };
  }
}

/**
 * Generate a raw queue: same attacker generation as normal,
 * with a pure Fisher-Yates shuffle ordering.
 */
export function generateRawQueue(
  grid: CellStack[][],
  seed: number,
  baseDifficulty: DifficultyConfig,
  megaBlockDefs?: MegaBlockDef[],
  blockadeWallDefs?: BlockadeWallDef[],
): AttackerQueueEntry[] {
  // Generate attackers with full base difficulty (no easing reduction)
  const queue = generateAttackerQueue(grid, seed, baseDifficulty, megaBlockDefs, blockadeWallDefs);

  // Pure Fisher-Yates shuffle — no reachability bias at all
  const rng = createRng(seed + 99999); // offset seed to avoid correlation with attacker generation
  for (let i = queue.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [queue[i], queue[j]] = [queue[j], queue[i]];
  }

  return queue;
}
